/**
 * @file StatementHandOverNotices.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-12
 * @modified 2026-10-20
 *
 * On Generate Statements: the statement runs colleagues have handed over to
 * you and you have not opened yet ("Cam Seymour handed over the September 2026
 * statements to you — <message>", with Open it / Dismiss), and "Your statement
 * runs" — every run you are on, to go back to one. And the runs you handed
 * over that nobody has opened yet, with "Take it back".
 */

import React, { useState } from 'react';
import Modal from './Modal';
import { useSlideOutButton } from './SlideOutButton';
import { handOverNoticeText, stageText, whenText, peopleText } from '../utils/statementRunWork';
import './StatementHandOver.css';

function RunsList({ runs, meId, currentRunId, openBlock, onOpen }) {
  useSlideOutButton([runs.length, openBlock]);
  if (!runs.length) return <p className="handover-hint">You are not on any other statement runs.</p>;
  return (
    <div className="handover-runs">
      {runs.map(r => {
        const isCurrent = Number(r.runId) === Number(currentRunId);
        const reason = isCurrent ? 'This run is already open' : openBlock;
        return (
          <div key={r.runId} className="handover-run-row">
            <div className="handover-run-main">
              <strong>{r.period?.label || `Run ${r.runId}`}</strong>
              <span>{stageText(r)}{r.statements ? ` · ${r.statements} statement${r.statements !== 1 ? 's' : ''}` : ''}</span>
              <span className="handover-hint">On it: {peopleText(r.people, meId) || 'just you'}{r.lastChangedAt ? ` · last change ${whenText(r.lastChangedAt)}` : ''}</span>
            </div>
            <div className="buttons slideout">
              <div>
                <button className="btn-slideout" onClick={() => onOpen(r)} disabled={!!reason} title={reason || 'Open this run on this screen'}>
                  <div className="icon-wrapper"><span className="material-symbols-rounded">open_in_new</span></div>
                  <span className="btn-label">Open</span>
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * @param {object} props
 * @param {Array} props.notices        unseen hand-overs (GET /api/statements/work/mine → notices)
 * @param {Array} props.runs           every run I am on (→ runs)
 * @param {number|null} props.currentRunId
 * @param {number} props.meId
 * @param {string|null} props.openBlock  why a run cannot be opened just now (a send in progress)
 * @param {function} props.onOpen      (run) — open it on this screen
 * @param {function} props.onDismiss   (run) — mark the notice seen
 * @param {Array} [props.handedAway]   runs I handed over that I can still take back (→ handedAway)
 * @param {function} [props.onTakeBack] (run)
 * @param {boolean} [props.inlineLauncher] - false: "Your statement runs" is opened from
 *        elsewhere (Generate Statements' header) through runsOpen / onRunsOpenChange
 * @param {boolean} [props.runsOpen]
 * @param {function} [props.onRunsOpenChange]
 */
function StatementHandOverNotices({
  notices = [], runs = [], currentRunId, meId, openBlock = null, onOpen, onDismiss, handedAway = [], onTakeBack,
  inlineLauncher = true, runsOpen, onRunsOpenChange
}) {
  const [ownShowRuns, setOwnShowRuns] = useState(false);
  const showRuns = runsOpen ?? ownShowRuns;
  const setShowRuns = onRunsOpenChange || setOwnShowRuns;
  const visible = notices.filter(n => Number(n.runId) !== Number(currentRunId));
  const others = runs.filter(r => Number(r.runId) !== Number(currentRunId));
  useSlideOutButton([visible.length, others.length, openBlock, handedAway.length]);

  if (!visible.length && !(inlineLauncher && others.length) && !handedAway.length && !showRuns) return null;
  return (
    <div className="handover-notices">
      {handedAway.map(h => (
        <div key={`away-${h.runId}`} className="handover-notice handover-notice-away" role="status">
          <span className="material-symbols-rounded handover-notice-icon">undo</span>
          <div className="handover-notice-text">
            <strong>
              You handed over the {h.period?.label || 'statement'} statements
              {(h.to || []).length ? ` to ${h.to.map(t => t.name).join(', ')}` : ''}
            </strong>
            <span className="handover-hint">
              {h.stranded ? 'Nobody is on it now.' : 'Not opened yet — you can still take it back.'}
              {h.handedOverAt ? ` · ${whenText(h.handedOverAt)}` : ''}
            </span>
          </div>
          <div className="buttons slideout">
            <div>
              <button className="btn-slideout" onClick={() => onTakeBack && onTakeBack(h)} disabled={!!openBlock}
                title={openBlock || 'Take the run back — it comes off their screen and back to you'}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">undo</span></div>
                <span className="btn-label">Take it back</span>
              </button>
            </div>
          </div>
        </div>
      ))}
      {visible.map(n => (
        <div key={n.runId} className="handover-notice" role="status">
          <span className="material-symbols-rounded handover-notice-icon">handshake</span>
          <div className="handover-notice-text">
            <strong>{handOverNoticeText(n)}</strong>
            <span className="handover-hint">{stageText(n)}{n.addedAt ? ` · ${whenText(n.addedAt)}` : ''}</span>
            {n.message && <blockquote className="handover-message">{n.message}</blockquote>}
          </div>
          <div className="buttons slideout">
            <div>
              <button className="btn-slideout" onClick={() => onDismiss(n)} data-read-safe title="Hide this notice — the run stays in Your statement runs">
                <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                <span className="btn-label">Dismiss</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={() => onOpen(n)} disabled={!!openBlock} title={openBlock || 'Open it on this screen'}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">open_in_new</span></div>
                <span className="btn-label">Open it</span>
              </button>
            </div>
          </div>
        </div>
      ))}
      {inlineLauncher && others.length > 0 && (
        <div className="handover-runs-launcher buttons slideout">
          <div>
            <button className="btn-slideout" onClick={() => setShowRuns(true)} data-read-safe>
              <div className="icon-wrapper"><span className="material-symbols-rounded">folder_shared</span></div>
              <span className="btn-label">Your statement runs ({others.length})</span>
            </button>
          </div>
        </div>
      )}
      {showRuns && (
        <Modal isOpen onClose={() => setShowRuns(false)} title="Your statement runs" size="md" className="statement-handover">
          <RunsList
            runs={others}
            meId={meId}
            currentRunId={currentRunId}
            openBlock={openBlock}
            onOpen={(r) => { setShowRuns(false); onOpen(r); }}
          />
        </Modal>
      )}
    </div>
  );
}

export default StatementHandOverNotices;
